import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Immutable from 'immutable';
import toast from 'react-hot-toast';

import {
  UI_OPERATION_STATUS_REQUEST,
  setOperationStatus,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_SUCCESS,
} from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableRootState, ImmutableEntity, ProfileEntity } from '@uncinc/react-drupal-core/types';
import { createOrUpdateUserProfile } from '../../../../store/actions/Profile';

import {
  createOrUpdateNewsletterContactSubscription,
  getNewsletterContactPropertyOptions,
  getNewsletterContact,
} from '../../../../store/actions/UI';

import { prepareSpotlerListData } from '../../../../helpers';
import { ProfileForm, getProfileValidationSchema } from '../../../../components/ProfileForm';

import { OTHER_FORM_ITEM_ID } from '../../../../config/constants';

import jobTitleOptions from '../../../../helpers/form_options/profile_job_titles.json';
import workFieldOptions from '../../../../helpers/form_options/profile_work_fields.json';

const PersonalAccountPage: React.FC = () => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [newInitialValues, setNewInitialValues] = useState({});
  const profileValidationSchema = getProfileValidationSchema();
  const uid = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], 0));
  const spotlerUserContact = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['uiInternal', 'newsletter', 'contact'], undefined));
  const spotlerUserContactSubscribed = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['uiInternal', 'newsletter', 'contact', 'is_subscribed'], undefined));
  // @ts-expect-error
  const userProfile = useSelector<ImmutableRootState<ImmutableEntity<ProfileEntity>>, ImmutableEntity<ProfileEntity>>((state) => state.getIn(['user', uid, 'profile'], Immutable.Map()));
  const userMail = useSelector<ImmutableRootState<string>>((state) => state.getIn(['user', uid, 'mail', 0, 'value'], ''));
  const profileValidationSchemaKeys = Object.keys(profileValidationSchema);

  const spotlerWorkFieldList = useSelector<ImmutableRootState<Array<any>>, Array<any>>((state) => state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list1'], undefined));
  const spotlerJobTitleList = useSelector<ImmutableRootState<Array<any>>, Array<any>>((state) => state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list2'], undefined));

  const profileUpdateStatus = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status'], ''));
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    // Load in profile fields.
    ...profileValidationSchema,
  });

  const onSubmit = (values: any) => {
    // Update the profile.
    const profileFormData = Object.fromEntries(
      Object.entries(values).filter(([key]) => !key.endsWith('_other')),
    );

    if (profileFormData.field_work_area === OTHER_FORM_ITEM_ID) {
      profileFormData.field_work_area = values.field_work_area_other;
    }

    if (profileFormData.field_job_title === OTHER_FORM_ITEM_ID) {
      profileFormData.field_job_title = values.field_job_title_other;
    }

    dispatch(createOrUpdateUserProfile(profileFormData) as any);

    // If we are subscribed, update Spotler.
    if (spotlerUserContactSubscribed) {
      // Prepare list data.
      const spotlerListData = prepareSpotlerListData(
        {
          fields: spotlerWorkFieldList,
          selected: values.field_work_area === OTHER_FORM_ITEM_ID
            ? values.field_work_area_other : values.field_work_area,
          selected_other: values.field_work_area_other,
        },
        {
          fields: spotlerJobTitleList,
          selected: values.field_job_title === OTHER_FORM_ITEM_ID
            ? values.field_job_title_other : values.field_job_title,
          selected_other: values.field_job_title_other,
        },
      );
      // Assemble values.
      const spotlerValues: any = {
        email: userMail,
        firstName: values.field_first_name,
        lastName: values.field_infix_name ? `${values.field_infix_name} ${values.field_last_name}` : values.field_last_name,
        organisation: values.field_organisation_name,
        ...spotlerListData,
      };
      dispatch(createOrUpdateNewsletterContactSubscription(spotlerValues) as any);
    }
  };

  // Try to fetch our Spotler account if we are logged in.
  useEffect(() => {
    if (!spotlerUserContact && userMail) {
      dispatch(getNewsletterContact(userMail) as any);
      dispatch(getNewsletterContactPropertyOptions() as any);
    }
  }, [userMail]);


  // Listens to the update status and shows a success or error toast message.
  useEffect(() => {
    if (profileUpdateStatus !== null) {
      if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
        toast.success(t('form.update_success'));
      } else if (profileUpdateStatus === UI_OPERATION_STATUS_ERROR) {
        toast.error(t('form.update_error'));
      }
      dispatch(setOperationStatus('profile', 'update', null, null));
    }
  }, [profileUpdateStatus, dispatch, t]);

  useEffect(() => {
    if (userProfile) {
      const initialValues: { [key: string]: any } = {};

      profileValidationSchemaKeys.forEach((key) => {
        initialValues[key] = userProfile.getIn([key, 0, 'value'], '');

        if (key === 'field_work_area_other') {
          const workArea = userProfile.getIn(['field_work_area', 0, 'value'], '');
          const knownWorkArea = workFieldOptions.find((e) => e.value === workArea);
          if (knownWorkArea === undefined) {
            initialValues.field_work_area = OTHER_FORM_ITEM_ID;
            initialValues.field_work_area_other = workArea;
          }
        }
        if (key === 'field_job_title_other') {
          const jobTitle = userProfile.getIn(['field_job_title', 0, 'value'], '');
          const knownJobTitle = jobTitleOptions.find((e) => e.value === jobTitle);
          if (knownJobTitle === undefined) {
            initialValues.field_job_title = OTHER_FORM_ITEM_ID;
            initialValues.field_job_title_other = jobTitle;
          }
        }
      });
      setNewInitialValues(initialValues);
    }
  }, [userProfile]);

  return (
    <div>
      <FormikWrapper
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        initialValues={newInitialValues}
        innerRef={formRef}
        // @ts-expect-error
        key={`profile-edit-${userProfile.size}-${JSON.stringify(newInitialValues)}`}
      >
        {/* Load in the profile form fields */}
        <ProfileForm
          formRef={formRef}
        />

        <div className="Form__actions">
          <Button
            type="submit"
            className={`Button Button--primary ${(profileUpdateStatus === UI_OPERATION_STATUS_REQUEST) ? 'Button--loading' : ''}`}
          >
            {t('general.save')}
          </Button>
        </div>
      </FormikWrapper>
    </div>
  );
};

export default PersonalAccountPage;
